import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    useTheme,
    Container,
    Paper,
    Chip,
    Stack,
    IconButton,
    Fade,
    Zoom,
} from '@mui/material';
import {
    Construction as ConstructionIcon,
    Schedule as ScheduleIcon,
    Notifications as NotificationsIcon,
    Assessment as AssessmentIcon,
    Folder as FolderIcon,
    Assignment as AssignmentIcon,
    Refresh as RefreshIcon,
    Home as HomeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppTheme } from '@contexts/ThemeContext';

interface ComingSoonProps {
    title: string;
    description: string;
    icon: React.ReactNode;
    color: string;
    expectedDate?: string;
    features?: string[];
}

const ComingSoon: React.FC<ComingSoonProps> = ({
    title,
    description,
    icon,
    color,
    expectedDate,
    features = []
}) => {
    const theme = useTheme();
    const { isDarkMode } = useAppTheme();
    const navigate = useNavigate();

    const handleGoHome = () => {
        navigate('/dashboard');
    };

    const handleRefresh = () => {
        window.location.reload();
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Fade in={true} timeout={1000}>
                <Box>
                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Zoom in={true} timeout={1500}>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 120,
                                    height: 120,
                                    borderRadius: '50%',
                                    background: isDarkMode
                                        ? `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`
                                        : `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
                                    border: `2px solid ${color}30`,
                                    mb: 3,
                                    position: 'relative',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: -4,
                                        left: -4,
                                        right: -4,
                                        bottom: -4,
                                        borderRadius: '50%',
                                        background: `linear-gradient(45deg, ${color}40, ${color}20)`,
                                        zIndex: -1,
                                        animation: 'pulse 2s infinite',
                                    },
                                    '@keyframes pulse': {
                                        '0%': {
                                            transform: 'scale(1)',
                                            opacity: 1,
                                        },
                                        '100%': {
                                            transform: 'scale(1.1)',
                                            opacity: 0,
                                        },
                                    },
                                }}
                            >
                                {icon}
                            </Box>
                        </Zoom>

                        <Typography
                            variant="h3"
                            sx={{
                                color: theme.palette.text.primary,
                                fontWeight: 'bold',
                                mb: 2,
                                background: `linear-gradient(45deg, ${color}, ${color}80)`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            {title}
                        </Typography>

                        <Typography
                            variant="h6"
                            sx={{
                                color: theme.palette.text.secondary,
                                maxWidth: 600,
                                mx: 'auto',
                                lineHeight: 1.6,
                            }}
                        >
                            {description}
                        </Typography>

                        {/* Coming Soon Message */}
                        <Box
                            sx={{
                                mt: 3,
                                p: 2,
                                borderRadius: 2,
                                background: isDarkMode
                                    ? `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`
                                    : `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
                                border: `1px solid ${color}30`,
                                textAlign: 'center',
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    color: color,
                                    fontWeight: 'bold',
                                    mb: 1,
                                }}
                            >
                                {title} page is coming soon!
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    fontStyle: 'italic',
                                }}
                            >
                                We're working hard to bring you this amazing feature
                            </Typography>
                        </Box>
                    </Box>

                    {/* Main Card */}
                    <Card
                        sx={{
                            maxWidth: 800,
                            mx: 'auto',
                            background: isDarkMode
                                ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)'
                                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                            backdropFilter: 'blur(10px)',
                            border: `1px solid ${color}30`,
                            boxShadow: `0 8px 32px ${color}20`,
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: 4,
                                background: `linear-gradient(90deg, ${color}, ${color}80)`,
                            },
                        }}
                    >
                        <CardContent sx={{ p: 4 }}>
                            {/* Status */}
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                <Chip
                                    icon={<ConstructionIcon />}
                                    label="Under Development"
                                    sx={{
                                        backgroundColor: `${color}20`,
                                        color: color,
                                        border: `1px solid ${color}40`,
                                        fontWeight: 600,
                                        px: 2,
                                        py: 1,
                                        fontSize: '0.9rem',
                                    }}
                                />
                            </Box>

                            {/* Features */}
                            {features.length > 0 && (
                                <Box sx={{ mb: 4 }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 600,
                                            mb: 2,
                                            textAlign: 'center',
                                        }}
                                    >
                                        Planned Features
                                    </Typography>
                                    <Stack spacing={1} direction="row" flexWrap="wrap" justifyContent="center">
                                        {features.map((feature, index) => (
                                            <Chip
                                                key={index}
                                                label={feature}
                                                variant="outlined"
                                                sx={{
                                                    borderColor: `${color}40`,
                                                    color: theme.palette.text.secondary,
                                                    '&:hover': {
                                                        backgroundColor: `${color}10`,
                                                        borderColor: color,
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            )}


                            {/* Action Buttons */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <IconButton
                                    onClick={handleGoHome}
                                    sx={{
                                        backgroundColor: `${color}20`,
                                        color: color,
                                        '&:hover': {
                                            backgroundColor: `${color}30`,
                                            transform: 'scale(1.05)',
                                        },
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    <HomeIcon />
                                </IconButton>
                                <IconButton
                                    onClick={handleRefresh}
                                    sx={{
                                        backgroundColor: `${color}20`,
                                        color: color,
                                        '&:hover': {
                                            backgroundColor: `${color}30`,
                                            transform: 'scale(1.05)',
                                        },
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Footer Message */}
                    <Box sx={{ textAlign: 'center', mt: 4 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.text.secondary,
                                fontStyle: 'italic',
                            }}
                        >
                            We're working hard to bring you amazing features. Stay tuned!
                        </Typography>
                    </Box>
                </Box>
            </Fade>
        </Container>
    );
};

export default ComingSoon;
